import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import RichTextEditor from "app/popleads/components/ui-kit/RichTextEditor";
import useProfileNoteActions from "app/popleads/hooks/useProfileNoteActions";
import { useExtensionContext } from "../ExtensionContext";
import { ExtensionBody, ExtensionSmall } from "app/popleads/components/ui-kit/Text";
import { useDropzone } from "react-dropzone";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { decideFileType } from "../../activities/utils";
import useGetProcessMimeMessage from "app/popleads/hooks/useGetProcessMimeMessage";
import highlightFromTo from "../utils/highlightFromTo";
import NotesFileCard, {
    Container,
    FileDetails,
    FileName,
} from "app/popleads/modules/extension/components/NotesFileCard";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import { ProfileLoadingOverlay } from "../ExtensionHome";
import { StyledCardFooter, StyledCardHeader } from "./common/styled";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import Modal from "app/popleads/components/ui-kit/Modal";
import removeTrailingEmptyNodes from "../utils/removeTrailingEmptyNodes";
import MaterialIcon from "app/popleads/components/ui-kit/MaterialIcon";

const EditNoteButtonContainer = styled.div`
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: end;
`;

const ExpandableBox = styled.div`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isExpanded }) => (isExpanded ? "unset" : 2)};
`;

export const StyledNoteContent = styled.div`
    color: ${({ variant }) => (variant === "warning" ? colors?.warning[800] : colors.neutral[800])};
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.17px;
    line-height: 21px;
    overflow-wrap: anywhere;
    ${({ variant }) => variant === "notEdit" && "cursor: pointer;"}
    & > p {
        margin: 0;
    }

    & i {
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        letter-spacing: inherit;
        line-height: inherit;
    }
`;

const StyledContentWrapper = styled.div``;

const StyledDragAndDrop = styled.div`
    padding: 8px 16px;
    font-size: 14px;
    background-color: ${colors.neutral[25]};
    border-width: 1px;
    border-style: dashed;
    border-color: ${({ isDragActive }) => (isDragActive ? colors.l3rnTech[900] : colors.neutral[300])};
    border-radius: 8px;
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const FileItem = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    margin-top: 12px;
    & > span {
        color: #8b999f;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
    }
`;

const StyledMailButton = styled("div")`
    font-size: 12px;
    font-weight: 400;
    color: ${colors.l3rnTech[900]};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

export const ShowMoreButton = styled.button`
    background: none;
    border: none;
    color: ${colors.l3rnTech[900]};
    cursor: pointer;
    padding: 4px 0;
    font-size: 12px;
    font-weight: 500;
    margin-top: 8px;
    display: block;
    &:hover {
        text-decoration: underline;
    }
`;

const StyledTooltip = styled(Tooltip)`
    .tooltip-inner {
        font-size: 11px !important;
    }
`;

const ExtensionNote = ({ note, variant }) => {
    const {
        profile,
        openMailModal,
        profileNotesRefetch,
        companyUsers,
        handleDeleteNote,
        refetchAttachments,
    } = useExtensionContext();
    const { updateNote, loading: noteLoading } = useProfileNoteActions();
    const [isEditing, setIsEditing] = useState(false);
    const [newNote, setNewNote] = useState(note.metin);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const wrapperRef = useRef(null);
    const { loading: processingMail, refetch: processMimeMessage } = useGetProcessMimeMessage(note?.mailId);
    const formRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef(null);

    const { user } = useSelector((state) => state.auth);
    const now = new Date();
    const fifteenMinutesInMillisecond = 15 * 60 * 1000;
    const fifteenMinutesAgo = now.getTime() - fifteenMinutesInMillisecond;
    const noteEditOrDeletePermission =
        user.id === note?.kayitciId && note?.zaman > fifteenMinutesAgo && note?.tip !== "otomatik";

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/plain": [".txt"],
            "video/mp4": [".mp4"],
            "text/csv": [".csv"],
            "image/svg+xml": [".svg"],
            "application/zip": [".zip"],
            "application/x-rar-compressed": [".rar"],
        },
        onDrop: (acceptedFiles) => {
            setSelectedFiles((prevFiles) =>
                [...prevFiles, ...acceptedFiles].map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );
        },
        onDropRejected: () => {
            toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
        },
    });

    function uploadPlugin(editor) {
        editor.editing.view.document.on("clipboardInput", (event, data) => {
            const files = Array.from(data.dataTransfer.files).filter((file) => {
                const ext = file.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                return (
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/png") ||
                    file.type.startsWith("application/pdf") ||
                    file.type.startsWith("application/msword") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
                    file.type.startsWith("application/vnd.ms-excel") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
                    file.type.startsWith("text/plain") ||
                    file.type.startsWith("video/mp4") ||
                    file.type.startsWith("text/csv") ||
                    file.type.startsWith("image/svg+xml") ||
                    file.type.startsWith("application/zip") ||
                    file.type.startsWith("application/x-zip-compressed") ||
                    file.type.startsWith("application/x-rar-compressed") ||
                    (file.type.startsWith("application/x-compressed") && (ext === "zip" || ext === "rar"))
                );
            });
            if (files.length !== data.dataTransfer.files.length) {
                toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
            }
            if (files.length > 0) {
                event.stop();
                setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
            }
        });
    }

    const mentionsFeed = companyUsers?.map((user) => {
        return {
            userId: user.id,
            text: user.name || user.ad,
            id: `@${user.name || user.ad}`,
        };
    });

    const editorConfig = {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
        placeholder: "Type your note *Activate mention by typing '@'",
        extraPlugins: [uploadPlugin],
        startupFocus: true,
        mention: {
            feeds: [
                {
                    marker: "@",
                    feed: mentionsFeed || [],
                    minimumCharacters: 1,
                },
            ],
        },
        link: {
            addTargetToExternalLinks: true,
        },
    };

    const handleSubmit = async () => {
        let sendNoteObj = {
            profil: profile.id,
            note: note.id,
            text: removeTrailingEmptyNodes(newNote),
        };

        if (selectedFiles && selectedFiles.length > 0) {
            const inputnames = [];
            selectedFiles.forEach((file, index) => {
                if (file && file.name) {
                    inputnames.push(`note${index}`);
                    sendNoteObj[`note${index}`] = file;
                }
            });
            if (inputnames.length > 0) {
                sendNoteObj.inputName = inputnames;
            }
        }

        const result = await updateNote(sendNoteObj);

        if (result?.error) {
            setNewNote(note.metin);
            // return;
        } else if (result?.data) {
            profileNotesRefetch({ rowsPerPage: 10, start: 0 });
            if (selectedFiles.length > 0) {
                //refetchProfile();
                refetchAttachments();
            }
        }

        setSelectedFiles([]);
        // setNote("");
        // addProfileNote(result.data.not);
    };

    useEffect(() => {
        if (note.metin !== newNote) {
            setNewNote(note.metin);
        }
    }, [note.metin]);

    const showMailContent = async () => {
        const content = await processMimeMessage();
        const newMailContent = document.createElement("div");
        newMailContent.innerHTML = content.replace("img/down.png", "/media/svg/popleads/download-cloud.svg");
        newMailContent.querySelectorAll("a").forEach((link) => {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
        });
        if (newMailContent) {
            openMailModal(newMailContent);
        }
    };
    const handleEditorReady = (editor) => {
        // Set the initial selection to the end of the document
        editor.model.change((writer) => {
            const endPosition = writer.createPositionAt(editor.model.document.getRoot(), "end");
            writer.setSelection(endPosition);
        });
        editor.editing.view.focus();

        editor.editing.view.document.on("keydown", (event, data) => {
            if (data.keyCode === 13) {
                if (!data.shiftKey) {
                    data.preventDefault();
                    if (formRef.current) {
                        formRef.current.dispatchEvent(new Event("submit", { cancelable: true }));
                        setIsEditing(false);
                    }
                } else {
                    const selection = editor.model.document.selection;
                    const currentPosition = selection.getLastPosition();
                    const currentBlock = currentPosition.parent;

                    if (currentBlock.name === "listItem" && currentBlock.isEmpty) {
                        editor.model.change((writer) => {
                            const paragraph = writer.createElement("paragraph");
                            writer.insert(paragraph, currentBlock, "after");
                            ///move cursor to new paragraph
                            writer.setSelection(paragraph, "in");

                            data.domEvent.preventDefault();
                        });
                        // Scroll to the bottom of the editor
                        const editable = editor.ui.view.editable.element;
                        editable.scrollTop = editable.scrollHeight;
                        return;
                    }
                    if (
                        editor.model.document.selection.anchor.parent.name === "listItem" ||
                        editor.model.document.selection.anchor.parent.name === "paragraph"
                    ) {
                        editor.execute("enter");
                    }
                    // Scroll to the bottom of the editor
                    const editable = editor.ui.view.editable.element;
                    editable.scrollTop = editable.scrollHeight;
                    data.domEvent.preventDefault();
                }
            }
        });
    };

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const isClamped = el.scrollHeight > el.clientHeight;
        setIsOverflowing(isClamped);
    }, [note.metin]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <OverlayTrigger placement="top" overlay={<StyledTooltip>Created Date</StyledTooltip>}>
                    <StyledCardHeader>{moment(note?.zaman).format("MMM DD, YYYY HH:mm")}</StyledCardHeader>
                </OverlayTrigger>
                {noteEditOrDeletePermission && !isEditing && (
                    <ButtonWithText
                        label="Delete note"
                        icon="trash-2"
                        iconType="feather"
                        size="circle"
                        variant="danger"
                        buttonStyle="borderless"
                        onClick={() => {
                            setModalOpen(true);
                        }}
                    />
                )}
            </div>
            {!isEditing ? (
                <div>
                    {noteLoading && (
                        <ProfileLoadingOverlay>
                            <Spinner size={32} thickness={4} variant="l3rnTech" />
                        </ProfileLoadingOverlay>
                    )}
                    <ExpandableBox ref={contentRef} isExpanded={isExpanded}>
                        <StyledNoteContent
                            variant={note.tip === "otomatik" ? "warning" : noteEditOrDeletePermission ? "notEdit" : ""}
                            dangerouslySetInnerHTML={{
                                __html:
                                    note.tip === "otomatik"
                                        ? highlightFromTo(note.metin)
                                        : note?.mailId && note.metin === ""
                                        ? "No Subject"
                                        : note.metin,
                            }}
                            onClick={() => {
                                if (note.tip === "otomatik") return;
                                if (noteEditOrDeletePermission) {
                                    setIsEditing(true);
                                    setIsExpanded(false);
                                }
                            }}
                        />
                    </ExpandableBox>
                    {isOverflowing && (
                        <ShowMoreButton onClick={() => setIsExpanded(!isExpanded)}>
                            {isExpanded ? "Show Less" : "Show More"}
                        </ShowMoreButton>
                    )}
                    <StyledCardFooter>Created by {note.kayitci}</StyledCardFooter>
                    <NotesFileCard variant="l3rnTech" attachments={note?.attachments} />

                    {note?.mailId && (
                        <StyledMailButton
                            onClick={showMailContent}
                            style={{ cursor: "pointer" }}
                            disabled={processingMail}
                        >
                            Show Mail
                        </StyledMailButton>
                    )}
                </div>
            ) : (
                isEditing && (
                    <StyledContentWrapper ref={wrapperRef}>
                        <form ref={formRef} onSubmit={handleSubmit}>
                            <RichTextEditor
                                variant="l3rnTech"
                                minHeight={"40px"}
                                maxHeight={"130px"}
                                data={newNote}
                                config={editorConfig}
                                onChange={(event, editor) => {
                                    const editorData = editor.getData();
                                    setNewNote(editorData);
                                }}
                                onReady={(editor) => {
                                    try {
                                        handleEditorReady(editor);
                                    } catch (error) {}
                                }}
                            />
                        </form>
                        <EditNoteButtonContainer>
                            <ButtonWithText
                                onClick={() => {
                                    setNewNote(note.metin);
                                    setSelectedFiles([]);
                                    setIsEditing(false);
                                }}
                                variant="danger"
                                buttonStyle="borderless"
                                size="small"
                            >
                                Cancel
                            </ButtonWithText>
                            <ButtonWithText
                                variant="l3rnTech"
                                buttonStyle="default"
                                type="default"
                                size="small"
                                onClick={async () => {
                                    handleSubmit();
                                    setIsEditing(false);
                                }}
                            >
                                Save
                            </ButtonWithText>
                        </EditNoteButtonContainer>

                        <NotesFileCard variant="l3rnTech" isEditing={isEditing} attachments={note?.attachments} />
                        {selectedFiles &&
                            selectedFiles.length > 0 &&
                            selectedFiles.map((file, index) => (
                                <FileItem key={index}>
                                    <Container>
                                        <FileDetails>
                                            <SVG src={toAbsoluteUrl(decideFileType(file?.name))} />
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <StyledTooltip id={`tooltip-${index}`}>{file.name}</StyledTooltip>
                                                }
                                            >
                                                <FileName style={{ maxWidth: "180px" }}>{file.name}</FileName>
                                            </OverlayTrigger>
                                        </FileDetails>
                                        <MaterialIcon
                                            icon="close"
                                            style={{ fontSize: "20px", cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                            }}
                                        />
                                    </Container>
                                </FileItem>
                            ))}
                        <StyledDragAndDrop {...getRootProps()} isDragActive={isDragActive}>
                            <input {...getInputProps()} />
                            <MaterialIcon icon="cloud_upload" />
                            <div>
                                <div>
                                    <ExtensionBody>Drag and Drop files here or</ExtensionBody>{" "}
                                    <ExtensionBody
                                        style={{
                                            color: colors.l3rnTech[900],
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                        }}
                                        onClick={open}
                                    >
                                        browse computer
                                    </ExtensionBody>
                                </div>
                                <ExtensionSmall style={{ color: colors.neutral[700] }}>
                                    File supported: DOC, XLS, TXT, PDF, JPEG, PNG
                                </ExtensionSmall>
                            </div>
                        </StyledDragAndDrop>
                    </StyledContentWrapper>
                )
            )}
            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Are you sure you want to delete this note?"
                variant="danger"
                icon="delete"
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onSuccess={() => {
                    handleDeleteNote(note.id);
                    setModalOpen(false);
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionNote;
